import { AxiosInstance, AxiosResponse } from "axios";
import { CarrierBridgeResponse } from "../CarrierBridgeData";
import type { CarrierBridgeDetailResponse } from "../CarrierBridgeDetails/CarrierBridgeDetailsData";
import type { BandwidthPoint } from "../CarrierBridgeDetails/CarrierBridgeBandwidthChart";

export interface CarrierBridgeListResponse {
  success: boolean;
  message?: string;
  data: CarrierBridgeResponse[];
}

export interface CarrierBridgeDetailEnvelope {
  success: boolean;
  message?: string;
  data: CarrierBridgeDetailResponse | null;
}

export interface CarrierBridgePerformanceEnvelope {
  success: boolean;
  message?: string;
  code?: string;
  data: {
    bandwidthUtilization: {
      inbound: BandwidthPoint[];
      outbound: BandwidthPoint[];
      unit: string;
    };
  } | null;
}

/**
 * The tenant's Carrier Bridge services, and its customers' for a partner
 * (the backend widens by child tenants when `includeChildServices` is set).
 * Drafts never reach the customer.
 */
export function fetchCarrierBridgeList(
  api: AxiosInstance,
  tenantId: number
): Promise<AxiosResponse<CarrierBridgeListResponse>> {
  return api.get<CarrierBridgeListResponse>("/kontroledge/carrier-bridge", {
    params: { tenantId, includeChildServices: true },
  });
}

/**
 * One Carrier Bridge with its status history. Another tenant's service
 * answers 404, so the caller passes the tenant the row belongs to.
 */
export function fetchCarrierBridgeDetail(
  api: AxiosInstance,
  id: number,
  tenantId: number
): Promise<AxiosResponse<CarrierBridgeDetailEnvelope>> {
  return api.get<CarrierBridgeDetailEnvelope>(
    `/kontroledge/carrier-bridge/${id}`,
    { params: { tenantId } }
  );
}

/**
 * Bandwidth measured on the customer side: the VLAN sub-interface, or the
 * bare port for a whole-port service. 422 `NO_MONITORING_TARGET` when the
 * port carries no monitoring data yet.
 */
export function fetchCarrierBridgePerformance(
  api: AxiosInstance,
  id: number,
  tenantId: number,
  timeRange: string,
  interval: string
): Promise<AxiosResponse<CarrierBridgePerformanceEnvelope>> {
  return api.get<CarrierBridgePerformanceEnvelope>(
    `/kontroledge/carrier-bridge/${id}/performance`,
    { params: { tenantId, timeRange, interval } }
  );
}

/**
 * Rename or retag a Carrier Bridge, the only edits open to the customer.
 * `tag` travels as the comma-separated string the legacy tag editor keeps;
 * the backend splits it.
 */
export function updateCarrierBridgeMeta(
  api: AxiosInstance,
  id: number,
  tenantId: number,
  data: { name?: string; tag?: string }
): Promise<AxiosResponse> {
  return api.patch(`/kontroledge/carrier-bridge/${id}`, data, {
    params: { tenantId },
  });
}
